/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { useIsMobile } from "hooks/useDeviceType";
import React, { useCallback, useMemo } from "react";
import { ActivityHeader } from "../../components/ActivityLayout/ActivityHeader";
import DownloadCSVButton from "../../components/DownloadCSVButton/DownloadCSVButton";
import { TransactionDetails } from "../../components/Modal/instance/TransactionDetail/TransactionDetails";
import { ActivityTable } from "../../components/TableContainer/ActivityTable";
import { Spacer } from "../../components/general/Spacer/Spacer";
import { Text } from "../../components/general/Text";
import { NETWORK_NAME_TO_DISPLAY } from "../../config/consts";
import { FlexBetweenContainer } from "../../containers/FlexBetweenContainer";
import { useIsTradeHistoryReturned } from "../../hooks/useIsTradeHistoryReturned";
import { useTimedFetchAllTokens } from "../../hooks/useTimedFetchAllTokens";
import { useTradeHistoryGroupedData } from "../../hooks/useTradeHistoryGroupedData";
import { useUpdateAddressWithENS } from "../../hooks/useUpdateAddressWithENS";
import { ReactComponent as DownloadIcon } from "../../img/download/DownloadItem.svg";
import { ActivityStore } from "../../store/activity/ActivityStore";
import { getAllTokens } from "../../store/activity/getAllTokens";
import { GlobalStore } from "../../store/global/GlobalStore";
import { getOrderTypeName } from "../../utils/orderTypeUtils";
import { ActivityContainer } from "./elements/ActivityContainer";
import { ConnectWalletCTA } from "./elements/ConnectWalletCTA";
import { ErrorDisplay } from "./elements/ErrorDisplay";
import { NoActivityYetDisplay } from "./elements/NoActivityYetDisplay";

export const Activity: React.FC = () => {
  const theme = GlobalStore.useState(s => s.theme);
  const userAddress = GlobalStore.useState(s => s.address);
  const history = ActivityStore.useState(s => s.history);

  const networkName = GlobalStore.useState(s => s.network.name);
  useUpdateAddressWithENS();
  /* Calls all 3 tokens endpoints eth, poly, arb for token decimals this is for /activity */
  getAllTokens.useBeckon(networkName);
  useTimedFetchAllTokens();

  const groupedData = useTradeHistoryGroupedData();
  const tableData = React.useMemo(
    () => (groupedData ? Object.values(groupedData).flat() : []),
    [groupedData]
  );
  const isMobile = useIsMobile();
  const isTradeHistoryReturned = useIsTradeHistoryReturned();

  const csvData = useMemo(() => {
    return tableData.map(item => {
      // We remove chain_id and status from the CSV
      const chainName = NETWORK_NAME_TO_DISPLAY[item.chain_id];
      return {
        "Time and Date": item.timestamp,
        "Tokens Sold": Object.keys(item.sellTokens).toString(),
        "Tokens Bought": Object.keys(item.buyTokens).toString(),
        "Volume (USD)": item.volumeUsd,
        "Network Name": chainName,
        "Transaction Hash": item.txHash,
        Trader: item.sender,
        Receiver: item.receiver,
        "Trade Type": getOrderTypeName(item.type),
        "Included Network Fee (USD)": item.gasFeeUsd,
      };
    });
  }, [tableData]);

  const downloadCsvFilename = useCallback(() => {
    const date = new Date();
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based, so we add 1
    const year = date.getFullYear();
    return `${day}${month}${year}`;
  }, []);

  const content = useMemo(() => {
    if (!userAddress) {
      return <ConnectWalletCTA />;
    }

    if (history.error != null) {
      return (
        <ActivityContainer theme={theme}>
          <ActivityHeader />
          <ErrorDisplay />
        </ActivityContainer>
      );
    }

    if (history.data != null && Object.keys(history.data.results).length === 0) {
      return <NoActivityYetDisplay />;
    }

    return (
      <ActivityContainer theme={theme}>
        <FlexBetweenContainer>
          <ActivityHeader />
          <DownloadCSVButton
            data={csvData}
            filename={`bebop_activity_${downloadCsvFilename()}`}
            isTradeHistoryReturned={isTradeHistoryReturned}
          >
            <Text
              size="Small"
              textStyle="Button"
              textCss={css`
                display: flex;
                align-items: center;
                gap: 2px;
                color: ${theme.button.tertiary.text.default};
              `}
            >
              {isMobile ? (
                <DownloadIcon
                  stroke={theme.button.tertiary.text.default}
                  width={20}
                  height={20}
                  style={{ marginRight: "8px" }}
                />
              ) : (
                <>
                  <DownloadIcon stroke={theme.button.tertiary.text.default} />
                  Download
                </>
              )}
            </Text>
          </DownloadCSVButton>
        </FlexBetweenContainer>
        <Spacer height={12} />
        <ActivityTable />
      </ActivityContainer>
    );
  }, [downloadCsvFilename, history, isMobile, isTradeHistoryReturned, csvData, theme, userAddress]);

  return (
    <>
      {content}
      <TransactionDetails />
    </>
  );
};
